/**
 * Status bar workflow step helpers — maps API assessment flags to `skippedStepKeys`
 * for `UnifiedStatusBar` (`label_value` keys from statusBarData).
 */

import type { StatusBarStep } from '@/types/statusBar.types';
import { DYNAMIC_STATUSES, DYNAMIC_STATUS_COLORS } from '@/utils/constants';

export const PROCEED_WITH_REVIEW_PANEL_API_FIELD = "proceedWithReviewPanel" as const;

/** Steps skipped when review panel workflow does not apply. */
export const REVIEW_PANEL_WORKFLOW_STEP_KEYS = [
  "AWAITING_BUSINESS_REVIEW",
  "READY_FOR_TASTING_PANEL",
  "TASTING_PANEL_APPROVED",
] as const;

/** Main tab indices disabled when review panel workflow does not apply. */
export const REVIEW_PANEL_DISABLED_MAIN_TAB_INDICES = [2, 3, 4] as const;

/** Redirect target when user lands on a disabled review-panel tab. */
export const REVIEW_PANEL_REDIRECT_MAIN_TAB_INDEX = 1;

/** API boolean false (`false`, `0`, `"false"`, `"0"`). */
export function isApiExplicitlyFalse(value: unknown): boolean {
  if (value === false) {
    return true;
  }
  if (value === 0) {
    return true;
  }
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "false" || v === "0";
  }
  return false;
}

/**
 * True when review panel steps should apply. Defaults to enabled when value is missing.
 */
export function isProceedWithReviewPanelEnabled(value: unknown): boolean {
  return !isApiExplicitlyFalse(value);
}

function readProceedWithReviewPanelFromRecord(
  record: Record<string, unknown>
): unknown {
  return record[PROCEED_WITH_REVIEW_PANEL_API_FIELD];
}

/**
 * Resolves `proceedWithReviewPanel` from common detail API payload shapes.
 * Returns `undefined` when the field is absent on all checked paths.
 */
export function resolveProceedWithReviewPanel(
  source: Record<string, unknown> | null | undefined
): boolean | undefined {
  if (source == null) {
    return undefined;
  }

  const direct = readProceedWithReviewPanelFromRecord(source);
  if (direct !== undefined) {
    return isProceedWithReviewPanelEnabled(direct);
  }

  const va = source.vendorAssessment;
  if (va != null && typeof va === "object" && !Array.isArray(va)) {
    const nested = readProceedWithReviewPanelFromRecord(va as Record<string, unknown>);
    if (nested !== undefined) {
      return isProceedWithReviewPanelEnabled(nested);
    }
  }

  if (Array.isArray(va) && va.length > 0) {
    const first = va[0];
    if (first != null && typeof first === "object" && !Array.isArray(first)) {
      const nested = readProceedWithReviewPanelFromRecord(first as Record<string, unknown>);
      if (nested !== undefined) {
        return isProceedWithReviewPanelEnabled(nested);
      }
    }
  }

  return undefined;
}

/**
 * `label_value` keys to mark skipped when review panel is explicitly disabled.
 */
export function getReviewPanelSkippedStepKeys(
  proceedWithReviewPanel?: boolean
): string[] {
  if (proceedWithReviewPanel === false) {
    return [...REVIEW_PANEL_WORKFLOW_STEP_KEYS];
  }
  return [];
}

export function getReviewPanelSkippedStepKeysFromSource(
  source: Record<string, unknown> | null | undefined
): string[] {
  return getReviewPanelSkippedStepKeys(resolveProceedWithReviewPanel(source));
}

/** True when `proceedWithReviewPanel` is explicitly false on the assessment payload. */
export function isReviewPanelWorkflowDisabledFromSource(
  source: Record<string, unknown> | null | undefined
): boolean {
  return resolveProceedWithReviewPanel(source) === false;
}

/** Merges multiple skip-key sources; dedupes. Returns `[]` when nothing to skip. */
export function composeSkippedStepKeys(
  ...sources: (string[] | undefined)[]
): string[] {
  const merged = sources.flatMap((s) => s ?? []);
  if (merged.length === 0) {
    return [];
  }
  return [...new Set(merged)];
}

type PayloadWithVendorAssessment = {
  vendorAssessment?: Record<string, unknown>;
};

/**
 * Merges getVendor `proceedWithReviewPanel` into a save payload.
 * Skips when the field was not present on the GET response.
 * Expects getVendor-shaped data already normalized by {@link normalizeVendorAssessmentInResponse}.
 */
export function withProceedWithReviewPanelFromGetApi<T extends PayloadWithVendorAssessment>(
  payload: T,
  proceedWithReviewPanelFromGet: boolean | undefined
): T {
  if (proceedWithReviewPanelFromGet === undefined) {
    return payload;
  }

  return {
    ...payload,
    vendorAssessment: {
      ...(payload.vendorAssessment ?? {}),
      [PROCEED_WITH_REVIEW_PANEL_API_FIELD]: proceedWithReviewPanelFromGet,
    },
  };
}

const NOT_PROGRESSED_STEP_COLOR =
  DYNAMIC_STATUS_COLORS[DYNAMIC_STATUSES.NOT_PROGRESSED];

/**
 * Inserts a "Not Progressed" step immediately before the workflow step matching
 * `currentStatus`. Not Progressed is the active step; the matched step and later steps are grayed.
 */
export function injectNotProgressedStep(
  steps: StatusBarStep[],
  currentStatus: string,
  label: string,
  findMatchingStepIndex: (status: string, steps: StatusBarStep[]) => number,
  inactiveStepColor: string,
): StatusBarStep[] {
  const workingSteps = steps.map((step) => ({ ...step }));
  const baseStepNo = Number(workingSteps[0]?.stepNo) || 1;

  const workflowIndex = findMatchingStepIndex(currentStatus, workingSteps);
  const insertIndex = workflowIndex !== -1 ? workflowIndex : workingSteps.length;

  const notProgressedStep: StatusBarStep = {
    label,
    label_value: 'NOT_PROGRESSED',
    stepNo: 0,
    stepperBackgroundColor: NOT_PROGRESSED_STEP_COLOR,
    isActive: false,
  };

  workingSteps.splice(insertIndex, 0, notProgressedStep);

  const notProgressedIndex = insertIndex;

  return workingSteps.map((step, index) => ({
    ...step,
    canonicalStepNo: step.canonicalStepNo ?? (Number(step.stepNo) || baseStepNo + index),
    stepNo: baseStepNo + index,
    isActive: index === notProgressedIndex,
    stepperBackgroundColor:
      index === notProgressedIndex
        ? NOT_PROGRESSED_STEP_COLOR
        : index > notProgressedIndex
          ? inactiveStepColor
          : step.stepperBackgroundColor,
  }));
}
